import { awaitJSON, HROASLambdaFetch } from '@src/utils/fetch-utils';
import { KpiRuns, ReleaseStatus } from '@src/Pages/MMM/MmmRunSelector/mmm-run-selector-constants';
import { HROAS_DEFAULT_KPI } from './hroasResults';

/**
 * Client + types for the two TypeScript `hroas` service endpoints that back the
 * HROAS run picker + publish UX:
 *   - GET  /hroas/list_hroas_runs        → HroasRunSummary[] (newest-first, all runs)
 *   - POST /hroas/on_hroas_run_published → the updated HroasRunSummary
 *
 * Mirrors the structure of `hroasResults.ts`. The list endpoint field is
 * `run_id` (snake_case); `getHroasResults` takes `runId` (camelCase) — convert
 * at the seam (see `hroasResults.ts`).
 */

/** One row of the `hroas_runs` audit table, as returned by `list_hroas_runs`. */
export interface HroasRunSummary {
  run_id: string;
  hroas_kpi: string;
  triggered_at: string | null;
  status: string; // 'succeeded' | 'submitted' | 'failed' | ...
  published: boolean;
  published_at: string | null;
  published_by: string | null;
  trigger_source: string | null;
  s3_output_prefix: string | null;
}

export const HROAS_RUNS_QUERY_KEY_PREFIX = 'hroasRuns';

/** Shared react-query key for the runs list — keyed on (company, kpi). */
export const getHroasRunsQueryKey = (
  company: string,
  hroasKpi: string = HROAS_DEFAULT_KPI
): readonly unknown[] => [HROAS_RUNS_QUERY_KEY_PREFIX, company, hroasKpi];

export const listHroasRuns = async (
  company: string,
  hroasKpi: string = HROAS_DEFAULT_KPI
): Promise<HroasRunSummary[]> => {
  const res = await HROASLambdaFetch('/list_hroas_runs', {
    method: 'GET',
    params: { cid: company, hroas_kpi: hroasKpi }
  });
  return awaitJSON<HroasRunSummary[]>(res);
};

export const setHroasRunPublished = async (
  company: string,
  runId: string,
  published: boolean
): Promise<HroasRunSummary> => {
  const res = await HROASLambdaFetch('/on_hroas_run_published', {
    method: 'POST',
    body: { cid: company, run_id: runId, published }
  });
  return awaitJSON<HroasRunSummary>(res);
};

/**
 * Default run selection: newest published (else newest succeeded). The list is
 * already ordered newest-first, so `find` returns the newest match. The
 * succeeded fallback is mandatory — nothing is published in this phase, so
 * without it the page would render blank.
 */
export const pickDefaultRun = (runs: HroasRunSummary[]): HroasRunSummary | undefined =>
  runs.find(run => run.published && run.status === 'succeeded') ??
  runs.find(run => run.status === 'succeeded');

/** Humanized run label: the trigger time, suffixed with the trigger source when present
 *  (e.g. "Jul 21, 2026, 2:14 PM · batch"). */
export const formatRunLabel = (run: HroasRunSummary): string => {
  const base = run.triggered_at
    ? new Date(run.triggered_at).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    : run.run_id;
  return run.trigger_source ? `${base} · ${run.trigger_source}` : base;
};

/**
 * Adapt the runs list (newest-first) to MmmRunSelector's shape: one group, since the outcome
 * variable is picked elsewhere. `published` maps to client-facing; the newest published run is
 * current. Runs are keyed by `run_id` (`id`), because `triggered_at` can be null or repeat.
 * Non-internal callers only ever see published runs.
 */
export const hroasToModelRuns = (
  runs: HroasRunSummary[],
  { isInternal, hroasKpi = HROAS_DEFAULT_KPI }: { isInternal: boolean; hroasKpi?: string }
): KpiRuns[] => {
  const visible = runs.filter(run => isInternal || run.published);
  if (visible.length === 0) return [];
  const currentId = visible.find(run => run.published)?.run_id;
  return [
    {
      value: hroasKpi,
      label: hroasKpi,
      runs: visible.map(run => ({
        id: run.run_id,
        kpi: hroasKpi,
        branch: '',
        refreshDate: run.triggered_at ?? run.run_id,
        releaseStatus: run.published ? ReleaseStatus.CLIENT_FACING : ReleaseStatus.UNMARKED,
        isCurrent: run.run_id === currentId,
        label: formatRunLabel(run)
      }))
    }
  ];
};

/**
 * Maps the toolbar's fixed "Outcome Variable" selection to the `hroas_kpi` used
 * to filter runs + results: "Revenue" → "revenue", "KPI Volume" → "non_revenue".
 * Anything else (including the pre-init `undefined`) falls back to the default
 * kpi, so the first render matches the "Revenue" top item.
 */
export const outcomeVariableToHroasKpi = (outcomeVariable: string | undefined): string =>
  outcomeVariable === 'KPI Volume' ? 'non_revenue' : HROAS_DEFAULT_KPI;
